import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocationPrefill } from '@/hooks/locations/useLocationPrefill';
import { isComplimentaryBillingOption } from '@/lib/membership/complimentary-option';
import { useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { TaxGroupOverrideDialog } from '@/components/tax/TaxGroupOverrideDialog';
import { estimateKeys } from '@/lib/queryKeys';
import { AlertTriangle, Loader2, RotateCcw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/atoms/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import { useEstimateAttachmentState } from '@/hooks/estimates/useEstimateAttachmentState';
import { useEstimateCustomerState } from '@/hooks/estimates/useEstimateCustomerState';
import { useEstimateCustomerTaxSync } from '@/hooks/estimates/useEstimateCustomerTaxSync';
import { useEstimateDraftState } from '@/hooks/estimates/useEstimateDraftState';
import { useEstimateDuplicateSeed } from '@/hooks/estimates/useEstimateDuplicateSeed';
import { useEstimateEditGuard } from '@/hooks/estimates/useEstimateEditGuard';
import { useEstimateEditHydration } from '@/hooks/estimates/useEstimateEditHydration';
import { useEstimateFinancialCalculations } from '@/hooks/estimates/useEstimateFinancialCalculations';
import { usePaymentScheduleTemplate } from '@/hooks/payment-schedule-templates/usePaymentScheduleTemplate';
import { useEstimateFormViewModel } from '@/hooks/estimates/useEstimateFormViewModel';
import { useEstimateFormResources } from '@/hooks/estimates/useEstimateFormResources';
import { useCopyOnCreateInheritance } from '@/hooks/custom-fields/useCopyOnCreateInheritance';
import {
  tmOverrideUnderquotedServices,
  useEstimateLineItemState,
} from '@/hooks/estimates/useEstimateLineItemState';
import { useLaborRateProfiles } from '@/hooks/pricebook/useDynamicPricing';
import { useNextEstimateNumber } from '@/hooks/estimates/useNextEstimateNumber';
import { useMembershipInfoWithInCart } from '@/hooks/estimates/useMembershipInfoWithInCart';
import { useMembershipPlanDetailsByIds } from '@/hooks/pricebook/useMembershipPlans';
import {
  membershipBillingOptionForSale,
  priceMembershipOptionForEquipment,
  resolveMembershipSaleEquipmentSelection,
} from '@/lib/membership/home-sale-pricing';
import { formatMembershipPlanSummary } from '@/lib/membership/format-plan-summary';
import { billingIntervalForOption } from '@/components/organisms/pricebook/support/membership-plan-shared';
import type { Equipment } from '@/types/equipment';
import { useEstimateUpsells, type EstimateUpsell } from '@/hooks/estimates/useEstimateUpsells';
import {
  toEstimateContactLike,
  useEstimatePrefillContact,
} from '@/hooks/estimates/useEstimatePrefillContact';
import { useEstimateSectionState } from '@/hooks/estimates/useEstimateSectionState';
import { useEstimateSettingsDefaults } from '@/hooks/estimates/useEstimateSettingsDefaults';
import { useEstimateSubmitFlow } from '@/hooks/estimates/useEstimateSubmitFlow';
import { useEstimateTaxResolution } from '@/hooks/estimates/useEstimateTaxResolution';
import { useEstimateTemplateSeed } from '@/hooks/estimates/useEstimateTemplateSeed';
import { SendConfirmModal } from '@/components/shared/SendConfirmModal';
import { Dialog, DialogContent, DialogTitle } from '@/components/atoms/dialog';
import { EstimateFormActions } from '@/components/organisms/estimates/EstimateFormActions';
import { EstimateFormSections } from '@/components/organisms/estimates/EstimateFormSections';
import { NewEstimatePaymentScheduleDialog } from '@/components/organisms/estimates/NewEstimatePaymentScheduleDialog';
import {
  draftPaymentPlanPreview,
  paymentScheduleTemplateToDraft,
  validateDraftEstimatePaymentSchedule,
  type DraftEstimatePaymentSchedule,
} from '@/components/organisms/payment-schedules/estimateTemplateValidation';
import { EstimateUpsellRecommendations } from '@/components/organisms/estimates/EstimateUpsellRecommendations';
import { MembershipOfferBlock } from '@/components/organisms/estimates/MembershipOfferBlock';
import {
  LineItemPicker,
  type MembershipLineItemAddRequest,
  type MembershipLineItemEditRequest,
  type PickedLineItem,
} from '@/components/organisms/LineItemPicker';
import { nonRepeatableLineItemIds } from '@/lib/sales/line-economics';
import {
  formatEstimateAddress,
  resolveEstimateSourceForDisplay,
  resolveJobTypeName,
  resolveCreateSalespersonId,
  toEditableLineItem,
  type EditableLineItem,
  deriveEstimateFormValuesFromEstimate,
} from '@/routes/_dashboard/dashboard/estimates/_shared';
import type { DisplayConfig } from '@/types/pricebook';
import { FormPageLayout } from '@/components/layouts';
import { useEstimateSettings } from '@/hooks/settings/useEstimateSettings';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { useTermsTemplates } from '@/hooks/settings/useTermsTemplates';
import { findDefaultTermsTemplate, toTermsPreviewFields } from '@/lib/terms';
import { useBillingSettings } from '@/hooks/settings/billing/useBillingSettings';
import { useActiveJobTypes } from '@/hooks/settings/useJobTypes';
import { useEstimateDocTemplates } from '@/hooks/estimateDocTemplates';
import { deriveBusinessUnitId } from '@/lib/business-units/derivedBusinessUnit';
import { filterByBusinessUnitScope } from '@/lib/business-units/businessUnitScope';
import { useBusinessUnit, useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import { useTaxPolicies } from '@/hooks/taxes/useTaxPolicies';
import { useTaxGroups } from '@/hooks/taxes/useTaxGroups';
import { useContact } from '@/hooks/contacts/useContact';
import {
  applyTaxPolicyPreview,
  resolvePreviewSegment,
  segmentFromContactType,
} from '@/lib/tax/tax-policy-preview';
import { useAuthUserId } from '@/stores/auth.store';
import { useLead } from '@/hooks/leads/useLead';
import type { SelectedLead } from '@/components/organisms/LeadSelector';
import { useSalesAttributionUsers } from '@/hooks/settings/useSalesAttributionUsers';
import { formatCustomFieldValueForPdf } from '@/components/organisms/custom-fields/custom-field-form-utils';
import type { PdfCustomFieldValue } from '@/components/documents/estimates/pdfTypes';
import { apiGet, apiPost, operationsClient } from '@/lib/api';
import { opsEstimateEndpoints } from '@/lib/api/endpoints';
import { customFieldKeys } from '@/lib/queryKeys';
import { resolveEstimateAddressValidation } from '@/lib/estimates/address-validation';
import {
  canSubmitEstimate,
  estimateSubmitBlockedReason,
  evaluateEstimateSubmitGate,
} from '@/lib/estimates/submit-gate';
import { resolveEstimateBusinessUnitGate } from '@/lib/estimates/business-unit-gate';
import {
  formatEstimateNameAddress,
  formatEstimateNameDate,
  generateEstimateNameFromTemplate,
  seededTitleShouldBeSent,
} from '@/lib/name-templates';
import { addressesMatch } from '@/lib/address-match';
import { useJob } from '@/hooks/jobs/useJob';
import type {
  CreateEstimateDto,
  CustomerApprovalMethod,
  Estimate,
  EstimateFulfillmentMode,
} from '@/types/estimate';
import type { Lead } from '@/types/lead';

const EstimatePDFPreview = lazy(
  () => import('@/components/documents/estimates/EstimatePDFPreview'),
);

// NOT-306 — resolve an evaluated upsell to an editable line, flagged so it
// persists `isUpsell`/`upsellContext` and renders the "Upsell" badge.
// `reason` defaults to the candidate's display copy but callers pass the copy of
// the specific source that made it actionable (auto vs recommend differ).
function upsellToEditable(upsell: EstimateUpsell, reason?: string | null): EditableLineItem {
  const s = upsell.suggestedItem;
  const context = reason !== undefined ? reason : upsell.reason;
  return {
    ...toEditableLineItem({
      id: s.id,
      itemVersion: s.itemVersion,
      name: s.name,
      code: s.code,
      type: s.type,
      unit: s.unit,
      description: s.description,
      basePrice: s.basePrice,
      laborRate: s.laborRate,
      costBasis: s.costBasis,
      markupPercent: s.markupPercent,
      priceStrategy: s.priceStrategy,
      taxable: s.taxable,
      taxLineType: s.taxLineType,
      displayConfig: (s.displayConfig as DisplayConfig | null) ?? null,
    }),
    isUpsell: true,
    upsellContext: context ?? undefined,
  };
}

// ─── EstimateFormPage ─────────────────────────────────────────────────────────

export function EstimateFormPage({
  editId,
  duplicateId,
  templateId,
  prefillContactId,
  prefillLocationId,
  prefillLeadId,
  sourceJobId,
  fulfillmentMode,
  originAppointmentId,
  selectedBusinessUnitId,
}: {
  editId?: string;
  duplicateId?: string;
  templateId?: string;
  prefillContactId?: string;
  prefillLocationId?: string;
  prefillLeadId?: string;
  // NOT-560 — set when this form was opened from a one-off job's "Create
  // estimate". Nothing is prefilled from the job (the operator quotes from
  // scratch); these only travel to the create payload so the saved estimate
  // links back to that job — and, from a visit, to that visit's technician.
  sourceJobId?: string;
  fulfillmentMode?: EstimateFulfillmentMode;
  originAppointmentId?: string;
  /**
   * The Business Unit picked on the "New Estimate" picker screen. It narrows
   * the job types offered here and is stamped onto the created estimate as
   * SELECTED. Absent means no choice was made: no filter, nothing stamped.
   */
  selectedBusinessUnitId?: string;
}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const companyTimeZone = useCompanyTimezone();
  const isEditMode = Boolean(editId);
  const { data: estimateSettings } = useEstimateSettings();
  const paymentSchedulesAllowed =
    estimateSettings?.paymentSchedule?.allowPaymentSchedules !== false;
  // Tax Policy layer (phase 4) — the MANUAL tax-group writer's dialog.
  const [taxGroupDialogOpen, setTaxGroupDialogOpen] = useState(false);
  const [paymentScheduleDialogOpen, setPaymentScheduleDialogOpen] = useState(false);
  const [draftPaymentSchedule, setDraftPaymentSchedule] =
    useState<DraftEstimatePaymentSchedule | null>(null);
  const [removeDuplicatedPaymentSchedule, setRemoveDuplicatedPaymentSchedule] = useState(false);
  // NOT-223: auto-expand the Payment schedule tile when a saved estimate
  // already has a template.
  const { data: existingScheduleTemplate } = usePaymentScheduleTemplate(editId ?? '', {
    enabled: isEditMode,
  });
  const { data: duplicateScheduleTemplate, isLoading: loadingDuplicateSchedule } =
    usePaymentScheduleTemplate(duplicateId, { enabled: Boolean(duplicateId) });
  const inheritedDuplicatePaymentSchedule = useMemo<DraftEstimatePaymentSchedule | null>(() => {
    if (!paymentSchedulesAllowed || !duplicateScheduleTemplate || removeDuplicatedPaymentSchedule) {
      return null;
    }
    return paymentScheduleTemplateToDraft(duplicateScheduleTemplate);
  }, [duplicateScheduleTemplate, paymentSchedulesAllowed, removeDuplicatedPaymentSchedule]);
  const effectiveDraftPaymentSchedule = paymentSchedulesAllowed
    ? (draftPaymentSchedule ?? inheritedDuplicatePaymentSchedule)
    : null;

  const { sections, toggleSection } = useEstimateSectionState();

  // ── Settings ─────────────────────────────────────────────────────────────
  const { data: billingSettings } = useBillingSettings();
  const {
    data: jobTypes = [],
    isLoading: jobTypesLoading,
    isError: jobTypesFailed,
  } = useActiveJobTypes();
  // Estimate Template Builder — assignable document templates for this doc type.
  // Only PUBLISHED templates are assignable: the customer-facing server path applies
  // PUBLISHED templates only, so offering a DRAFT would let the office assign one the
  // customer would never see (office renders the draft, customer gets the tenant default).
  const { data: allDocTemplates } = useEstimateDocTemplates({ docType: 'ESTIMATE' });
  const docTemplates = useMemo(
    () => (allDocTemplates ?? []).filter((t) => t.status === 'PUBLISHED'),
    [allDocTemplates],
  );
  const [jobTypeId, setJobTypeId] = useState('');
  const [poNumber, setPoNumber] = useState('');
  // NOT-542 — Business Unit. On an estimate this is ordinary data entry rather
  // than a reassignment: anyone who can write the estimate may pick the unit
  // while it has no line items, with no justification. The moment the estimate
  // has a line the choice freezes (see `businessUnitLocked`), which is also
  // enforced server-side.
  const businessUnitsQuery = useBusinessUnits('active');
  const hasNoBusinessUnits = businessUnitsQuery.isSuccess && businessUnitsQuery.data.length === 0;
  // The picker already validated this selection before opening the create form.
  // Edit and duplicate hydrate their own unit from the saved estimate below.
  const [businessUnitId, setBusinessUnitId] = useState<string | null>(() =>
    editId || duplicateId ? null : (selectedBusinessUnitId ?? null),
  );

  // ── Contact selection ────────────────────────────────────────────────────
  const {
    contactId,
    setContactId,
    billToId,
    setBillToId,
    customerName,
    setCustomerName,
    customerEmail,
    setCustomerEmail,
    customerPhone,
    setCustomerPhone,
    primaryContactName,
    setPrimaryContactName,
    primaryContactTitle,
    setPrimaryContactTitle,
    customerAddress,
    setCustomerAddress,
    contactAddresses,
    setContactAddresses,
    addressMode,
    setAddressMode,
    selectedAddressIndex,
    setSelectedAddressIndex,
    showAllAddresses,
    setShowAllAddresses,
    saStreet,
    setSaStreet,
    saStreet2,
    setSaStreet2,
    saCity,
    setSaCity,
    saState,
    setSaState,
    saZip,
    setSaZip,
    billingMode,
    setBillingMode,
    selectedBillingIndex,
    setSelectedBillingIndex,
    showAllBillingAddresses,
    setShowAllBillingAddresses,
    baStreet,
    setBaStreet,
    baStreet2,
    setBaStreet2,
    baCity,
    setBaCity,
    baState,
    setBaState,
    baZip,
    setBaZip,
    applyAddress,
    selectContact,
    clearContact,
    selectedCustomer,
  } = useEstimateCustomerState();
  const [showAddressValidationErrors, setShowAddressValidationErrors] = useState(false);

  // ── Tax resolution ──────────────────────────────────────────────────────
  const {
    taxResolution,
    taxLoading,
    resolveTax,
    handleZipChange,
    clearPendingTaxResolution,
    setTaxResolution,
  } = useEstimateTaxResolution({ city: saCity, state: saState });

  const { applyAddressWithTax, selectContactWithTax, clearContactWithTax } =
    useEstimateCustomerTaxSync({
      applyAddress,
      selectContact,
      clearContact,
      resolveTax,
      clearPendingTaxResolution,
      setTaxResolution,
    });

  // ── Line items ───────────────────────────────────────────────────────────
  const {
    lineItems,
    setLineItems,
    addPickedLineItem,
    addConfiguredServicePart,
    removeConfiguredServicePart,
    removeLineItem,
    updateQuantity,
    updateName,
    updateType,
    updateUnit,
    updateBasePrice,
    updateManualPriceOverride,
    updatePriceStrategy,
    applyPricingMode,
    updateLaborRate,
    updateEstimatedHours,
    updateLaborCostLines,
    updateDynamicPricingConfig,
    updateDynamicMarkupProfile,
    updateDescription,
    updateEquipmentAssociation,
    updateMembershipEquipmentAssociation: updateMembershipEquipmentAssociationState,
    updateComponentDisplayMode,
    updateMarkupPercent,
    updateCostBasis,
    updateTaxable,
    updateTaxAccounting,
    updateMemberPricing,
    linkLineToCatalogItem,
    updateChargeable,
    updateHiddenFromCustomer,
    toggleOptional,
    toggleSelected,
    reorderLineItems,
  } = useEstimateLineItemState();
  const [membershipEditRequest, setMembershipEditRequest] =
    useState<MembershipLineItemEditRequest | null>(null);
  const [membershipAddRequest, setMembershipAddRequest] =
    useState<MembershipLineItemAddRequest | null>(null);

  const openMembershipConfigurator = useCallback(
    (index: number) => {
      const item = lineItems[index];
      if (!item?.membershipPlanId) {
        toast.error('This membership does not have a plan configuration to edit.');
        return;
      }
      const picked: PickedLineItem = {
        ...item,
        description: item.description ?? '',
        taxLineType: item.taxLineType ?? null,
        membershipPlanId: item.membershipPlanId,
        membershipBillingOptionId: item.membershipBillingOptionId ?? undefined,
      };
      setMembershipEditRequest({ requestId: Date.now(), rowId: item._rowId, item: picked });
    },
    [lineItems],
  );

  const applyMembershipConfiguration = useCallback(
    (picked: PickedLineItem) => {
      const rowId = membershipEditRequest?.rowId;
      if (!rowId) return;
      setLineItems((current) =>
        current.map((item) =>
          item._rowId === rowId
            ? {
                ...item,
                ...picked,
                _rowId: item._rowId,
                lineKey: item.lineKey ?? picked.lineKey,
                parentLineKey: item.parentLineKey,
              }
            : item,
        ),
      );
      setMembershipEditRequest(null);
    },
    [membershipEditRequest?.rowId, setLineItems],
  );

  const membershipPlanIds = useMemo(
    () =>
      Array.from(
        new Set(
          lineItems.flatMap((item) =>
            typeof item.membershipPlanId === 'string' && item.membershipPlanId
              ? [item.membershipPlanId]
              : [],
          ),
        ),
      ),
    [lineItems],
  );
  const { plansById: membershipPlansById, statusById: membershipPlanStatusById } =
    useMembershipPlanDetailsByIds(membershipPlanIds, {
      enabled: membershipPlanIds.length > 0,
    });
  const membershipEquipmentPricingStatusByRowId = useMemo(
    () =>
      new Map(
        lineItems.map((item) => [
          item._rowId,
          item.membershipPlanId
            ? (membershipPlanStatusById.get(item.membershipPlanId) ?? 'loading')
            : 'ready',
        ]),
      ),
    [lineItems, membershipPlanStatusById],
  );
  const updateMembershipEquipmentAssociation = useCallback(
    (
      index: number,
      equipmentIds: string[],
      locationOverrideReason: string | undefined,
      selectedEquipment: Equipment[],
    ) => {
      const item = lineItems[index];
      const plan = item?.membershipPlanId
        ? membershipPlansById.get(item.membershipPlanId)
        : undefined;
      const option = plan
        ? membershipBillingOptionForSale(plan, item?.membershipBillingOptionId)
        : undefined;
      if (!item) {
        return;
      }
      if (!plan || !option) {
        // Older estimate rows may predate the persisted plan/option link. There
        // is no authoritative pricing input to recalculate from, but accepting
        // an equipment edit and then dropping it is worse: preserve the
        // association without pretending the legacy snapshot was repriced.
        updateEquipmentAssociation(index, equipmentIds, locationOverrideReason);
        return;
      }

      const resolvedEquipment = resolveMembershipSaleEquipmentSelection(
        equipmentIds,
        selectedEquipment.map((unit) => ({
          id: unit.id,
          category: String(unit.category ?? ''),
          name: unit.name ?? null,
          serialNumber: unit.serialNumber ?? null,
        })),
      );
      if (!resolvedEquipment) {
        toast.error(
          'Some selected equipment could not be loaded. Refresh before changing coverage.',
        );
        return;
      }

      const soldHomePricing = priceMembershipOptionForEquipment({
        option,
        coverageRules: plan.coverageRules,
        equipment: resolvedEquipment,
      });
      const interval = billingIntervalForOption(option);
      const selectedPlan = {
        ...plan,
        priceAmount: soldHomePricing.priceAmount,
        billingIntervalValue: interval.billingIntervalValue,
        billingIntervalUnit: interval.billingIntervalUnit,
        contractLengthMonths: option.termMonths || null,
      };
      updateMembershipEquipmentAssociationState(
        index,
        // Pricing intentionally ignores equipment categories this plan does
        // not cover. Persist that same filtered set so the row cannot claim a
        // covered unit that contributed neither coverage nor price.
        soldHomePricing.equipmentIds,
        locationOverrideReason,
        soldHomePricing.priceAmount,
        formatMembershipPlanSummary(selectedPlan, {
          complimentaryOption: isComplimentaryBillingOption(option),
        }),
      );
    },
    [
      lineItems,
      membershipPlansById,
      updateEquipmentAssociation,
      updateMembershipEquipmentAssociationState,
    ],
  );

  // Configured labor rates, fetched ONCE for the editor and handed down: the
  // flat-service labor block fills its hourly COST from a profile's cost rate,
  // and a per-row query would be one request per line.
  const { data: laborRateProfiles = [] } = useLaborRateProfiles();

  // NOT-605 — membership context for the preview: an active membership on the
  // contact, or a prospective one synthesized from an in-cart MEMBERSHIP line
  // (join-and-save), so the live total matches the backend-saved estimate.
  const {
    membershipInfo,
    isSettled: membershipSettled,
    hasError: membershipPricingError,
    retry: retryMembershipPricing,
    inCartOverriddenByActive,
    activeMembershipPlanIds,
  } = useMembershipInfoWithInCart(contactId, lineItems);

  const {
    estimateNumber,
    setEstimateNumber,
    title,
    setTitle,
    linkedLeadId,
    setLinkedLeadId,
    source,
    setSource,
    leadSource,
    setLeadSource,
    leadSourceId,
    setLeadSourceId,
    salespersonId,
    setSalespersonId,
    tagIds,
    setTagIds,
    scopeOfWork,
    setScopeOfWork,
    notes,
    setNotes,
    discountValue,
    setDiscountValue,
    discountType,
    setDiscountType,
    discountReason,
    setDiscountReason,
    promotionId,
    setPromotionId,
    pricingMode,
    setPricingMode,
    depositValue,
    setDepositValue,
    depositType,
    setDepositType,
    depositRequired,
    setDepositRequired,
    bookingType,
    setBookingType,
    recurringFrequency,
    setRecurringFrequency,
    recurringInterval,
    setRecurringInterval,
    recurringIntervalUnit,
    setRecurringIntervalUnit,
    recurringStartDate,
    setRecurringStartDate,
    recurringDaysOfWeek,
    setRecurringDaysOfWeek,
    recurringEndType,
    setRecurringEndType,
    recurringEndAfterCount,
    setRecurringEndAfterCount,
    recurringEndOnDate,
    setRecurringEndOnDate,
    validUntil,
    setValidUntil,
    customerApprovalMethod,
    setCustomerApprovalMethod,
    internalNotes,
    setInternalNotes,
    terms,
    setTerms,
    docTemplateId,
    setDocTemplateId,
    requireSignature,
    setRequireSignature,
    selectedTemplateId,
    applyDraftState,
    applyTemplateDefaults,
  } = useEstimateDraftState({
    expirationDays: estimateSettings?.expirationDays,
    templateId,
    setCustomerName,
    setCustomerEmail,
    setCustomerPhone,
    setPrimaryContactName,
    setPrimaryContactTitle,
    setCustomerAddress,
    setContactId,
    setLineItems,
    setJobTypeId,
  });

  // ── Mutations & queries ──────────────────────────────────────────────────
  const {
    createEstimate,
    submitUpdateEstimate,
    sendEstimate,
    upsertValues,
    estimateCustomFields,
    customFields,
    activeFieldKeys,
    setCustomField,
    setActiveFieldKeys,
    removeField,
    applyInheritedDraftValues,
    existingEstimate,
    loadingEstimate,
    duplicateEstimate,
    loadingDuplicate,
    selectedTemplate,
  } = useEstimateFormResources({
    isEditMode,
    editId,
    duplicateId,
    selectedTemplateId,
  });
  useEffect(() => {
    const sourceEstimate = (existingEstimate ?? duplicateEstimate) as Estimate | undefined;
    if (!sourceEstimate) return;
    setJobTypeId(sourceEstimate.jobTypeId ?? '');
    setPoNumber(sourceEstimate.poNumber ?? '');
    // NOT-542 — hydrate the effective Business Unit; when inherited it equals the
    // job type's unit so the control reads as "inherited".
    setBusinessUnitId(sourceEstimate.businessUnitId ?? null);
  }, [duplicateEstimate, existingEstimate]);
  // NOT-542 — the unit the estimate carries without a pin: the server-derived
  // unit of an existing estimate, else the job type's only unit, else none (a
  // multi-unit type is resolved at save, and any explicit pick pins it).
  // The job types OFFERED in the picker. `jobTypes` stays complete on purpose:
  // it also resolves an already-chosen type's name and business units, and a
  // filtered lookup would blank those out for a type outside the picked unit.
  // Nothing is selected ⇒ nothing is filtered; an empty `businessUnitIds` on a
  // job type is a wildcard, so unscoped types stay in every unit's list.
  const selectableJobTypes = useMemo(
    () => filterByBusinessUnitScope(jobTypes, selectedBusinessUnitId),
    [jobTypes, selectedBusinessUnitId],
  );
  const businessUnitCandidateIds = useMemo(
    () =>
      hasNoBusinessUnits
        ? []
        : (jobTypes.find((jobType) => jobType.id === jobTypeId)?.businessUnitIds ?? []),
    [hasNoBusinessUnits, jobTypes, jobTypeId],
  );
  const derivedBusinessUnitId = useMemo(() => {
    if (hasNoBusinessUnits) return null;
    const persisted = existingEstimate as Estimate | undefined;
    return deriveBusinessUnitId({
      candidateIds: businessUnitCandidateIds,
      persistedBusinessUnitId:
        persisted && (persisted.jobTypeId ?? '') === jobTypeId
          ? (persisted.businessUnitId ?? null)
          : null,
      persistedBusinessUnitSource: persisted?.businessUnitSource ?? null,
    });
  }, [businessUnitCandidateIds, existingEstimate, hasNoBusinessUnits, jobTypeId]);
  const effectiveBusinessUnitId = hasNoBusinessUnits ? null : businessUnitId;
  const pricebookBusinessUnitId =
    estimateSettings?.filterPricebookByBusinessUnit !== false ? effectiveBusinessUnitId : null;

  // ── Tax Policy layer: live preview ──────────────────────────────────────
  // The ZIP resolves the RATES; the winning tax rule decides what they apply
  // to. Mirror the server's matcher here so the totals react as the operator
  // switches business unit / job type / customer — the save then prices
  // identically on the backend (the authority).
  const { data: taxPolicies } = useTaxPolicies();
  const anySegmentRule = (taxPolicies ?? []).some((rule) => rule.segment !== null);
  const { data: taxContactDetail } = useContact(contactId ?? '', {
    enabled: Boolean(contactId) && anySegmentRule,
  });
  const { data: allTaxGroupsForPin } = useTaxGroups();
  // The tax preview must know the SELECTED unit's segment even when the
  // unit is archived: a saved estimate can reference an inactive BU that
  // the active-only picker list will never contain, and the resolver still
  // reads that BU row on save. Hydrate it individually as the fallback.
  const buForTaxId = effectiveBusinessUnitId ?? derivedBusinessUnitId;
  const selectedUnitQuery = useBusinessUnit(buForTaxId ?? null);
  const liveTaxResolution = useMemo(() => {
    const buForTax = buForTaxId;
    const contactSegment = segmentFromContactType(
      (taxContactDetail as { contactType?: string | null } | undefined)?.contactType,
    );
    // The full precedence — unit decides, ANY defers to the contact, an
    // unloaded unit is UNKNOWN (skip narrowing, never flash the contact
    // rule), an archived unit resolves via the individually hydrated row —
    // lives in resolvePreviewSegment, where it is unit-tested directly.
    const { segment: previewSegment, known: buSegmentKnown } = resolvePreviewSegment({
      buForTax,
      unitFromList: businessUnitsQuery.data?.find((bu) => bu.id === buForTax),
      hydratedUnit: selectedUnitQuery.data ?? null,
      contactSegment,
    });
    // A MANUAL pin wins over geography — server parity. The pinned group is
    // previewed from the hydrated /tax-groups list; if it cannot be found
    // (stale cache) the address preview stands in until refetch.
    const persisted = existingEstimate as Estimate | undefined;
    const pinnedGroupId =
      persisted?.taxResolvedFrom === 'MANUAL' ? (persisted?.taxGroupId ?? null) : null;
    const pinnedGroup = pinnedGroupId
      ? ((allTaxGroupsForPin ?? []).find((g) => g.id === pinnedGroupId) ?? null)
      : null;
    // Exemption still beats the pin — server parity: resolveTax returns
    // the exempt verdict before the manual group is even consulted.
    const baseResolution =
      pinnedGroup && !taxResolution?.exempt
        ? { exempt: false, group: pinnedGroup, resolvedFrom: 'MANUAL' as const }
        : taxResolution;
    if (!buSegmentKnown) return baseResolution;
    return applyTaxPolicyPreview(
      baseResolution,
      taxPolicies,
      {
        businessUnitId: buForTax,
        segment: previewSegment,
        jobTypeId: jobTypeId || null,
        stateCode: saState ? saState.toUpperCase() : null,
        zipCode: saZip || null,
      },
      allTaxGroupsForPin,
    );
  }, [
    taxResolution,
    taxPolicies,
    effectiveBusinessUnitId,
    derivedBusinessUnitId,
    businessUnitsQuery.data,
    taxContactDetail,
    jobTypeId,
    saState,
    existingEstimate,
    allTaxGroupsForPin,
    selectedUnitQuery.data,
  ]);
  // NOT-542 — see resolveEstimateBusinessUnitGate for why "may the user edit
  // this?" and "may this save state it?" are deliberately separate questions.
  const { locked: businessUnitLocked, savable: businessUnitSavable } =
    resolveEstimateBusinessUnitGate({
      isEditMode,
      editorLineCount: effectiveBusinessUnitId ? lineItems.length : 0,
      persistedLineCount: (
        (existingEstimate as Estimate | undefined)?.lineItems as unknown[] | undefined
      )?.length,
      spawnedFromJob: Boolean((existingEstimate as Estimate | undefined)?.originJobId),
    });
  // Saved attribution may be immutable; do not strand existing unscoped estimates.
  const businessUnitBlockedReason =
    hasNoBusinessUnits || effectiveBusinessUnitId || (isEditMode && businessUnitLocked)
      ? null
      : businessUnitsQuery.isPending
        ? 'Loading business units…'
        : businessUnitsQuery.isError
          ? 'Could not load business units. Reload to continue.'
          : 'Select a business unit to continue.';

  const selectedJobTypeName = useMemo(
    () =>
      resolveJobTypeName(
        jobTypeId,
        jobTypes,
        ((existingEstimate ?? duplicateEstimate) as Estimate | undefined)?.jobTypeName,
      ),
    [duplicateEstimate, existingEstimate, jobTypeId, jobTypes],
  );

  // ── Pre-fill estimate # and title (NOT-218) ──────────────────────────────
  // Preview the next number (read-only — never reserves it) so the field is
  // populated on open. `*Dirty` flags stop auto-derivation once the user edits.
  const nextNumberQuery = useNextEstimateNumber({ enabled: !isEditMode });
  const [estimateNumberDirty, setEstimateNumberDirty] = useState(false);
  const [titleDirty, setTitleDirty] = useState(false);

  const handleEstimateNumberChange = useCallback(
    (value: string) => {
      setEstimateNumberDirty(true);
      setEstimateNumber(value);
    },
    [setEstimateNumber],
  );
  const handleTitleChange = useCallback(
    (value: string) => {
      setTitleDirty(true);
      setTitle(value);
    },
    [setTitle],
  );
  /**
   * The document's Time & materials switch — the MODE and the LINES together.
   *
   * The mode alone rewrote every eligible service to DYNAMIC at pricing time and
   * left the document holding a shape neither engine reads as a materialized
   * service, so both derived the price from the service line's own labor rate
   * and markup and the parts it carries contributed nothing (see
   * `applyPricingMode`). The lines are materialized here instead, at the moment
   * the operator asks for T&M pricing, so what the mode produces is an ordinary
   * materialized dynamic service that every reader already prices identically.
   * Switching back hands the parked flat pricing straight back.
   */
  const changePricingMode = useCallback(
    (mode: 'STANDARD' | 'TM_OVERRIDE') => {
      setPricingMode(mode);
      applyPricingMode(mode);
    },
    [setPricingMode, applyPricingMode],
  );
  /**
   * Services this estimate is quoting WITHOUT their parts — the shape every
   * TM_OVERRIDE estimate saved before the switch materialized anything still
   * holds, and which nothing here repairs on its own.
   *
   * Hydration deliberately does not run `applyPricingMode`. This estimate's
   * `grandTotal` and per-line `lineSubtotal` are PERSISTED; the public quote page
   * and the PDF read those stored figures, and an approved estimate's is
   * countersigned into an immutable PDF. Repricing on open would change the
   * number the customer is holding without anyone deciding to — and a price
   * change on a quote that has gone out is a REVISION, which this product
   * already has a flow for. So the editor names the problem and offers the
   * repair; the operator takes it or does not.
   */
  const underquotedTMServices = useMemo(
    () =>
      pricingMode === 'TM_OVERRIDE'
        ? tmOverrideUnderquotedServices(lineItems).map((line) => line.name)
        : [],
    [pricingMode, lineItems],
  );
  const repriceTMFromParts = useCallback(() => {
    applyPricingMode('TM_OVERRIDE');
  }, [applyPricingMode]);
  // Seed the number preview for new + duplicated estimates (their number is
  // intentionally blank); never touches edit mode or a user override.
  useEffect(() => {
    if (isEditMode || estimateNumberDirty) return;
    const next = nextNumberQuery.data?.estimateNumber;
    if (next && !estimateNumber.trim()) setEstimateNumber(next);
  }, [isEditMode, estimateNumberDirty, nextNumberQuery.data, estimateNumber, setEstimateNumber]);

  // Seed the title default: NOT-584's `{job type} — {street}`, falling back to
  // `Estimate {EST-#}` while neither is known. Skips edit + duplicate
  // (duplicate keeps the copied title); re-derives while the user hasn't edited
  // it, so picking a job type or an address later still names the draft.
  useEffect(() => {
    if (isEditMode || duplicateId || titleDirty) return;
    const next = nextNumberQuery.data?.estimateNumber ?? '';
    // Through the shared helper, not a local join: the backend builds this same
    // token from the same three fields, and only one of the two ever renders a
    // given estimate — so a difference here is a title that silently depends on
    // where it was raised. Joining raw kept a whitespace-only field that the
    // backend trims away.
    const serviceAddress = formatEstimateNameAddress({
      street: saStreet,
      city: saCity,
      state: saState,
    });
    // Not the browser locale: this title is SENT, so it becomes stored data,
    // and an en-GB browser would otherwise file "4/8/2026" against the same
    // template, tenant and day that an API create files as "8/4/2026". The
    // helper pins 'en-US' the way the backend renderer does; the tenant's
    // timezone still picks the day.
    const date = formatEstimateNameDate(new Date(), companyTimeZone);
    const derived = generateEstimateNameFromTemplate({
      template: estimateSettings?.estimateNameTemplate,
      customerName,
      estimateNumber: next,
      // Same source of truth as the backend renderer, which reads
      // dto.jobTypeName then the resolved JobType row — never metadata.
      jobType: selectedJobTypeName || null,
      date,
      // Service address only — the title names WHERE THE WORK HAPPENS, so the
      // bill-to address is not a stand-in for it. It also has to match what the
      // backend renders from the same payload: `customer.address` is the
      // billing address there too, and seeding the title from it showed the
      // user "AC replacement — 640 Quayside" while the estimate saved as
      // "AC replacement".
      address: serviceAddress || null,
      // The street line the {short_address} token wants. Left to the renderer
      // to derive from the joined address when only a one-line address exists.
      shortAddress: saStreet || null,
    });
    setTitle((prev) => (prev === derived ? prev : derived));
  }, [
    isEditMode,
    duplicateId,
    titleDirty,
    nextNumberQuery.data,
    estimateSettings?.estimateNameTemplate,
    selectedJobTypeName,
    customerName,
    saStreet,
    saCity,
    saState,
    companyTimeZone,
    setTitle,
  ]);

  const titleNamesTheWork = seededTitleShouldBeSent(
    title,
    nextNumberQuery.data?.estimateNumber,
    // The template decides whether this seed is ours to persist: one that uses
    // {estimate_number} bakes in a preview the server may not hand out, so it
    // stays server-rendered.
    estimateSettings?.estimateNameTemplate,
    // Same question for {date}: until this resolves, the seed above rendered
    // the day in the browser's timezone, not the tenant's. Withhold rather
    // than persist a date that depends on who happened to open the form.
    companyTimeZone,
  );

  // ── Default T&C (NOT-222) ────────────────────────────────────────────────
  // A brand-new estimate starts on the tenant's default repository entry. Seeds
  // at most once, and never over an edit, a duplicate, or a selection the user
  // (or the estimate template) already made.
  const { data: termsTemplates } = useTermsTemplates();
  const didSeedTermsRef = useRef(false);
  useEffect(() => {
    if (isEditMode || duplicateId) return;
    if (didSeedTermsRef.current) return;
    if (terms.termsAndConditions.trim() || terms.termsMode !== 'DEFAULT') return;
    const fallback = findDefaultTermsTemplate(termsTemplates ?? []);
    if (!fallback) return;
    didSeedTermsRef.current = true;
    setTerms({
      termsMode: 'DEFAULT',
      termsTemplateIds: [fallback.id],
      termsAndConditions: fallback.content,
    });
  }, [isEditMode, duplicateId, terms, termsTemplates, setTerms]);

  // ── Attachments ──────────────────────────────────────────────────────────
  const {
    attachments,
    internalAttachments,
    setInternalAttachments,
    pendingAttachmentId,
    excludedTemplateAttachmentIds,
    excludedDuplicateAttachmentIds,
    handleCustomerAttachmentsChange,
  } = useEstimateAttachmentState({
    isEditMode,
    editId,
    templateId,
    duplicateId,
  });

  // COPY_ON_CREATE: pre-fill estimate custom fields (editable) from the selected
  // contact and service address. The picked address entity id is only known in
  // "select" mode (a manually-typed address is not a known entity to inherit from).
  const inheritanceServiceAddressId =
    addressMode === 'select' && selectedAddressIndex != null
      ? (contactAddresses[selectedAddressIndex]?.id ?? null)
      : null;
  useCopyOnCreateInheritance({
    enabled: !isEditMode,
    targetSchemas: estimateCustomFields,
    sourceIds: { contact: contactId, address: inheritanceServiceAddressId },
    apply: applyInheritedDraftValues,
  });
  const prefillLeadQuery = useLead(prefillLeadId ?? '', {
    enabled: Boolean(prefillLeadId) && !isEditMode,
  });
  const selectedLeadQuery = useLead(linkedLeadId, {
    enabled: Boolean(linkedLeadId),
  });
  const linkedLeadRepId = selectedLeadQuery.data?.assignedTo ?? prefillLeadQuery.data?.assignedTo;
  // NOT-509: the Salesperson picker lists only sales-attribution candidates
  // (roles with estimates:create), so finance-only roles like accountant are
  // excluded — not the whole roster.
  //
  // NOT-584: plus the linked lead's rep, who the roster's company scope would
  // otherwise hide in a multi-company tenant — the conversion path inherits
  // that rep tenant-wide, and this list is the only thing the inheritance below
  // consults, so without them a cross-company lead silently pays the creator
  // instead. Widening the list also gives the picker their name to render.
  const usersQuery = useSalesAttributionUsers({ includeUserId: linkedLeadRepId });
  const users = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);
  const currentUserId = useAuthUserId();
  // NOT-584 — a converted lead brings its rep with it. The person who worked
  // the lead owns the sale; attributing it to whoever happened to open the form
  // credits the wrong rep on every estimate raised from someone else's lead.
  // This narrows NOT-509's "never inherit an assigned rep" to the no-lead case;
  // its candidate filter still applies, so a lead assigned to a role that
  // cannot own an estimate falls back to the creator rather than through.
  //
  // Only while the field is untouched: once a human picks a salesperson, a
  // late-arriving lead query must not overwrite them. `salespersonId` cannot
  // answer that — the effect seeds the creating user on first render, so the
  // field is never empty by the time the lead resolves.
  //
  // The latch is deliberately one-way. Linking a lead after picking a rep does
  // not re-inherit: the pick is the more specific answer, and silently moving
  // the commission because someone attached a lead afterwards is the louder
  // failure of the two.
  const salespersonTouchedRef = useRef(false);
  const handleSalespersonChange = useCallback(
    (value: string) => {
      salespersonTouchedRef.current = true;
      setSalespersonId(value);
    },
    [setSalespersonId],
  );
  // A new estimate is otherwise attributed to the user creating it. The picker
  // stays editable, but every create-mode path that would leave the field empty
  // (fresh form, duplicate seed, cleared contact) falls back to the current
  // user.
  //
  // NOT-509: the candidate list is roles with estimates:create, so a salesperson
  // carried in from elsewhere may not be valid (e.g. an accountant). Once the
  // list has loaded, drop a non-candidate so an invalid attribution is never
  // submitted. Both rules are create-only — edit preserves the estimate's saved
  // value for display (it may predate them).
  useEffect(() => {
    if (isEditMode) return;
    const next = resolveCreateSalespersonId({
      current: salespersonId,
      preferred: salespersonTouchedRef.current ? null : linkedLeadRepId,
      currentUserId,
      candidateIds: users.map((user) => user.id),
      candidatesLoaded: usersQuery.isSuccess,
    });
    if (next !== salespersonId) setSalespersonId(next);
  }, [
    isEditMode,
    users,
    usersQuery.isSuccess,
    salespersonId,
    setSalespersonId,
    currentUserId,
    linkedLeadRepId,
  ]);
  // The linked lead is searched via LeadSelector; these queries only hydrate the
  // selected chip (from `linkedLeadId`) and the `?leadId=` prefill.
  const selectedLead = useMemo<SelectedLead | null>(() => {
    const lead = selectedLeadQuery.data ?? prefillLeadQuery.data ?? null;
    if (!lead) return null;
    return {
      id: lead.id,
      leadNumber: lead.leadNumber,
      label: lead.title || lead.serviceRequested || lead.leadNumber,
      contactName: lead.contact?.displayName,
    };
  }, [selectedLeadQuery.data, prefillLeadQuery.data]);
  const didPrefillLeadRef = useRef(false);

  useEstimateEditGuard({
    isEditMode,
    editId,
    existingEstimate: existingEstimate as Estimate | undefined,
    onRedirect: (id) =>
      void router.navigate({
        to: '/dashboard/estimates/$id',
        params: { id },
        search: { tab: 'overview' } as never,
      }),
  });

  // Which fields an applied template has authored. Shared with the settings
  // defaults below so the two cannot fight over the same field depending on
  // which query resolves first — see the hook for the ordering argument.
  const templateAuthoredFieldsRef = useRef<Set<string>>(new Set());

  useEstimateSettingsDefaults({
    estimateSettings,
    isEditMode,
    duplicateId,
    billingDepositPercent: billingSettings?.deposits?.defaultPercent,
    setNotes,
    setDepositValue,
    setDepositType,
    setDepositRequired,
    setRequireSignature,
    setCustomerApprovalMethod,
    setValidUntil,
    templateAuthoredFieldsRef,
  });

  /**
   * NOT-584 (A4) — the approval method is picked in the send modal, and
   * `signatureRequired` is derived from it. handleSubmitAndSend seeds that
   * derivation once, BEFORE the modal opens; without re-deriving here, picking
   * SIGNATURE in the modal would send an estimate whose stored
   * `signatureRequired` is still false — a signature-approval estimate that
   * never asks for a signature.
   */
  const handleApprovalMethodChange = useCallback(
    (value: CustomerApprovalMethod | '') => {
      setCustomerApprovalMethod(value);
      // Clearing to "Not specified" re-derives too. The send now clears the
      // stored method for `''`, and the modal hides the signature toggle for
      // anything but SIGNATURE — so holding capture on here would leave the
      // estimate demanding a signature under no method, with no visible
      // control to undo it.
      setRequireSignature(value === 'SIGNATURE');
    },
    [setCustomerApprovalMethod, setRequireSignature],
  );

  const { rehydrate: rehydrateEstimateForm } = useEstimateEditHydration({
    isEditMode,
    existingEstimate: existingEstimate as Estimate | undefined,
    applyDraftState,
    applyAddress: applyAddressWithTax,
    setContactId,
    setBillToId,
    setContactAddresses,
    setAddressMode,
    setSelectedAddressIndex,
    setSaStreet,
    setSaStreet2,
    setSaCity,
    setSaState,
    setSaZip,
    setBillingMode,
    setSelectedBillingIndex,
    setCustomerAddress,
    setBaStreet,
    setBaStreet2,
    setBaCity,
    setBaState,
    setBaZip,
  });

  const effectivePrefillContactId = prefillLeadQuery.data?.contactId ?? prefillContactId;
  useEstimatePrefillContact({
    prefillContactId: effectivePrefillContactId,
    isEditMode,
    onSelectContact: selectContactWithTax,
    isCustomerAlreadySet: () => Boolean(contactId),
  });

  useLocationPrefill({
    locationId: prefillLocationId,
    ownerId: prefillContactId,
    contactId,
    addresses: contactAddresses,
    enabled: !isEditMode && !duplicateId && !sourceJobId,
    onUnavailable: () => {
      setSelectedAddressIndex(null);
      setAddressMode('manual');
      applyAddressWithTax({});
    },
    onSelect: (index, address) => {
      setSelectedAddressIndex(index);
      setAddressMode('select');
      applyAddressWithTax(address);
    },
  });

  // NOT-560 — an estimate quoted off a job is for the SITE that job is at, and
  // for the PERSON the job is being done for. Both are job context, not job
  // scope, so they still come across on a form that starts empty:
  //
  //  - Service address. The customer prefill seeds the contact's DEFAULT
  //    address, which is the wrong one for any customer with more than one
  //    property — and the service address is what resolves tax.
  //  - `serviceContact`. A job raised for an onsite contact or a tenant names
  //    that person, not the account owner. The server-prefilled spawn used it
  //    for the estimate's customer snapshot (buildEstimatePrefillFromJob), so
  //    it has to survive here too.
  //
  // Silent on failure: a deleted or inaccessible source job should not greet
  // the operator with a fetch error on a form that otherwise works.
  const { data: sourceJob, isLoading: loadingSourceJob } = useJob(
    !isEditMode && sourceJobId ? sourceJobId : '',
    { showErrorToast: false },
  );
  // Which customer selection the job context was last applied over. `undefined`
  // is "never applied" and is deliberately distinct from `null` (applied while
  // no customer was chosen), so the first pass still runs on a job that has no
  // linked customer.
  const seededSourceJobForContactRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (isEditMode || !sourceJobId || loadingSourceJob) return;
    // Wait for a PENDING customer prefill to land. selectContact rewrites the
    // address and the customer fields wholesale, so seeding before it arrives
    // would simply be overwritten. (No prefill requested → nothing to wait for.)
    if (effectivePrefillContactId && contactId !== effectivePrefillContactId) return;
    // Re-apply on every customer CHANGE rather than once. A job with no linked
    // customer reaches this with contactId null, and the customer the operator
    // then picks calls selectContact — which would overwrite the job's site
    // with that customer's default address and keep it. Keying on the selection
    // also re-asserts the site if they switch customers again. A manual edit to
    // the address does not change contactId, so operator edits are never
    // clobbered.
    if (seededSourceJobForContactRef.current === contactId) return;

    const jobAddress = sourceJob?.serviceAddress;
    const jobBilling = sourceJob?.billingAddress;
    const serviceContact = sourceJob?.serviceContact;
    if (!jobAddress?.street && !jobBilling?.street && !serviceContact?.name) return;

    seededSourceJobForContactRef.current = contactId;

    // The job's own saved row, by its location id when it names one: two sites
    // can share a street, and a retired one must not land on its lookalike.
    const jobRowIndex = (
      snapshot: { street?: string } & Parameters<typeof addressesMatch>[1],
      rowId: string | null | undefined,
    ) =>
      rowId
        ? contactAddresses.findIndex((address) => address.id === rowId)
        : contactAddresses.findIndex((address) => addressesMatch(address, snapshot));

    if (jobAddress?.street) {
      // Keep the picker honest: point it at the customer's matching saved
      // address when there is one, otherwise drop to manual so the operator
      // sees the address the estimate will actually carry instead of a selected
      // entry that says something else.
      const matchingIndex = jobRowIndex(jobAddress, sourceJob?.locationId ?? jobAddress.addressId);
      if (matchingIndex >= 0) {
        setSelectedAddressIndex(matchingIndex);
        setAddressMode('select');
      } else {
        setSelectedAddressIndex(null);
        setAddressMode('manual');
      }
      applyAddressWithTax(jobAddress);
    }

    // Billed where the job is billed. Left to the customer prefill, an estimate
    // raised on a job billed somewhere else billed the customer's default, and
    // approving it back onto the job overwrote the job's billing with that.
    // A job with no billing of its own bills the customer — the default stays.
    if (jobBilling?.street) {
      const billingIndex = jobRowIndex(jobBilling, jobBilling.addressId);
      if (billingIndex >= 0) {
        setBillingMode('select');
        setSelectedBillingIndex(billingIndex);
        setCustomerAddress(formatEstimateAddress(contactAddresses[billingIndex]));
      } else {
        setBillingMode('manual');
        setSelectedBillingIndex(null);
        setBaStreet(jobBilling.street ?? '');
        setBaStreet2(jobBilling.street2 ?? '');
        setBaCity(jobBilling.city ?? '');
        setBaState(jobBilling.state ?? '');
        setBaZip(jobBilling.zip ?? '');
      }
    }

    // Per FIELD, matching the server snapshot's precedence: the job's person
    // wins where it has a value, and the owner contact fills the rest.
    if (serviceContact?.name) setCustomerName(serviceContact.name);
    if (serviceContact?.email) setCustomerEmail(serviceContact.email);
    if (serviceContact?.phone) setCustomerPhone(serviceContact.phone);
  }, [
    applyAddressWithTax,
    contactAddresses,
    contactId,
    effectivePrefillContactId,
    isEditMode,
    loadingSourceJob,
    setAddressMode,
    setCustomerEmail,
    setCustomerName,
    setCustomerPhone,
    setSelectedAddressIndex,
    setBillingMode,
    setSelectedBillingIndex,
    setCustomerAddress,
    setBaStreet,
    setBaStreet2,
    setBaCity,
    setBaState,
    setBaZip,
    sourceJob,
    sourceJobId,
  ]);

  useEffect(() => {
    const lead = prefillLeadQuery.data;
    if (!lead || isEditMode || didPrefillLeadRef.current) return;

    didPrefillLeadRef.current = true;
    setLinkedLeadId(lead.id);
    setSource('INBOUND_LEAD');
    setLeadSource(lead.source ?? '');
    setLeadSourceId(lead.sourceId ?? '');
    if (!notes.trim() && lead.description) {
      setNotes(lead.description);
    }
  }, [
    prefillLeadQuery.data,
    isEditMode,
    notes,
    setLeadSource,
    setLeadSourceId,
    setLinkedLeadId,
    setNotes,
    setSource,
  ]);

  // NOT-584 — the mechanism belongs to the row from the moment it is created.
  // These handlers fire in edit mode too (the Linked-lead control stays live
  // there), so without this an office user who links a lead to a
  // proposal-sourced estimate rewrites its source to INBOUND_LEAD on save — and
  // since the picker is gone, nothing can put it back. The by-source report
  // groups on exactly this field.
  const stampSource = useCallback(
    (next: string | ((current: string) => string)) => {
      if (isEditMode) return;
      setSource(next as Parameters<typeof setSource>[0]);
    },
    [isEditMode, setSource],
  );

  // Unlinking a lead has to give its rep back. `salespersonId` still holds the
  // inherited value and is a valid candidate, so `resolveCreateSalespersonId`
  // would keep it on the next pass — leaving an estimate with no lead, a MANUAL
  // source and a rep it has no connection to. Untouched only: a human's own
  // pick is theirs and survives the lead going away.
  //
  // Create-only, like `stampSource` and the inheritance effect above. On an
  // EXISTING estimate `salespersonId` is not an inherited guess this form made
  // — it is the estimate's saved attribution, possibly set months ago by
  // someone else, and the untouched ref says nobody has re-answered rather than
  // that the value is up for grabs. Nothing reseeds it in edit mode either, so
  // clearing here would wipe a stored rep for good on an edit that merely
  // swapped the contact.
  const releaseInheritedSalesperson = useCallback(() => {
    if (isEditMode) return;
    if (salespersonTouchedRef.current) return;
    setSalespersonId('');
  }, [isEditMode, setSalespersonId]);

  const handleSelectContact = useCallback(
    (contact: Parameters<typeof selectContactWithTax>[0]) => {
      selectContactWithTax(contact);
      if (linkedLeadId) {
        setLinkedLeadId('');
        setLeadSource('');
        setLeadSourceId('');
        stampSource((current) => (current === 'INBOUND_LEAD' ? 'MANUAL' : current));
        releaseInheritedSalesperson();
      }
    },
    [
      linkedLeadId,
      selectContactWithTax,
      setLeadSource,
      setLeadSourceId,
      setLinkedLeadId,
      stampSource,
      releaseInheritedSalesperson,
    ],
  );

  const handleClearContact = useCallback(() => {
    clearContactWithTax();
    setLinkedLeadId('');
    setLeadSource('');
    setLeadSourceId('');
    stampSource((current) => (current === 'INBOUND_LEAD' ? 'MANUAL' : current));
    releaseInheritedSalesperson();
  }, [
    clearContactWithTax,
    setLeadSource,
    setLeadSourceId,
    setLinkedLeadId,
    stampSource,
    releaseInheritedSalesperson,
  ]);

  const handleResetBillingToService = useCallback(() => {
    const serviceAddress =
      addressMode === 'manual'
        ? {
            street: saStreet,
            street2: saStreet2,
            city: saCity,
            state: saState,
            zip: saZip,
          }
        : selectedAddressIndex === null
          ? null
          : contactAddresses[selectedAddressIndex];

    setSelectedBillingIndex(null);
    setBillingMode('select');
    setBaStreet('');
    setBaStreet2('');
    setBaCity('');
    setBaState('');
    setBaZip('');
    setBillToId(null);
    setCustomerAddress(formatEstimateAddress(serviceAddress));
  }, [
    addressMode,
    contactAddresses,
    saCity,
    saState,
    saStreet,
    saStreet2,
    saZip,
    selectedAddressIndex,
    setBaCity,
    setBaState,
    setBaStreet,
    setBaStreet2,
    setBaZip,
    setBillToId,
    setBillingMode,
    setCustomerAddress,
    setSelectedBillingIndex,
  ]);

  const handleLinkedLeadSelect = useCallback(
    (lead: Lead) => {
      setLinkedLeadId(lead.id);
      stampSource('INBOUND_LEAD');
      setLeadSource(lead.source ?? '');
      setLeadSourceId(lead.sourceId ?? '');
      if (lead.contactId && lead.contactId !== contactId) {
        apiGet<Record<string, unknown>>(operationsClient, `/contacts/${lead.contactId}`)
          .then((contact) => selectContactWithTax(toEstimateContactLike(contact)))
          .catch(() => {});
      }
    },
    [contactId, selectContactWithTax, setLeadSource, setLeadSourceId, setLinkedLeadId, stampSource],
  );

  const handleClearLinkedLead = useCallback(() => {
    setLinkedLeadId('');
    setLeadSource('');
    setLeadSourceId('');
    stampSource((current) => (current === 'INBOUND_LEAD' ? 'MANUAL' : current));
    releaseInheritedSalesperson();
  }, [setLeadSource, setLeadSourceId, setLinkedLeadId, stampSource, releaseInheritedSalesperson]);

  useEstimateDuplicateSeed({
    duplicateId,
    duplicateEstimate: duplicateEstimate as Estimate | undefined,
    applyDraftState,
    applyAddress: applyAddressWithTax,
    setBillToId,
    setContactAddresses,
    setAddressMode,
    setSelectedAddressIndex,
    setBillingMode,
    setSelectedBillingIndex,
    setCustomerAddress,
    setBaStreet,
    setBaStreet2,
    setBaCity,
    setBaState,
    setBaZip,
  });

  useEstimateTemplateSeed({
    selectedTemplate,
    setNotes,
    setLineItems,
    setTerms,
    applyTemplateDefaults,
    templateAuthoredFieldsRef,
  });

  const {
    lineItemPreviews,
    subtotal,
    taxTotal,
    taxBreakdown,
    discountAmount,
    recurringConfigPayload,
    membershipDiscountPreview,
    membershipEligibleRowIds,
    membershipOffer,
    grandTotal,
    depositEligibleTotal,
    depositAmount,
  } = useEstimateFinancialCalculations({
    lineItems,
    taxResolution: liveTaxResolution,
    pricingMode,
    discountValue,
    discountType,
    membershipInfo,
    depositValue,
    depositType,
    bookingType,
    recurringFrequency,
    recurringInterval,
    recurringIntervalUnit,
    recurringStartDate,
    recurringDaysOfWeek,
    recurringEndType,
    recurringEndAfterCount,
    recurringEndOnDate,
    membershipCustomerId: contactId,
    membershipServiceAddressId: inheritanceServiceAddressId,
    membershipOfferEnabled: membershipSettled && Boolean(contactId),
    ownedMembershipPlanIds: activeMembershipPlanIds,
    // A distinct membership already selected on this estimate is part of the
    // current price. Compare a recommendation against that combined baseline,
    // not against pre-cart benefits, or its advertised savings are inflated.
    membershipOfferBaselineInfo: membershipInfo,
  });

  const recommendedMembershipAlreadyAdded = Boolean(
    membershipOffer && lineItems.some((line) => line.membershipPlanId === membershipOffer.planId),
  );
  const openRecommendedMembership = useCallback((planId: string) => {
    setMembershipAddRequest({ requestId: Date.now(), planId });
  }, []);

  // ── Upsells (NOT-306, spec §8) ─────────────────────────────────────────────
  const { upsells } = useEstimateUpsells({
    lineItems,
    estimateTotal: grandTotal,
    // Hold evaluation until the membership lookup settles for this contact — an
    // in-flight "no membership yet" must not be evaluated as a confirmed
    // non-member (would auto-insert non-member upsells for a real member).
    enabled: membershipSettled,
    // The evaluator uses the literal 'NONE' sentinel for customers without a
    // membership, so a no-membership customer must send 'NONE' (not null) or
    // non-member-only upsells never match. Members send their plan name
    // (best-effort); asset context is not wired in v1.
    membershipStatus: membershipInfo?.planName ?? 'NONE',
    asset: null,
  });

  // Source items the user added via the picker THIS session. Auto-insert only
  // fires for these — opening an existing estimate/duplicate/template must not
  // mutate the lines (the feature is "execute upsells on add", not on load).
  const userAddedSourceIds = useRef<Set<string>>(new Set());
  // (sourceLineItemId::suggestedItemId) pairs an auto-upsell has already fired
  // for. Keyed by the PAIR, not just the suggested item: a removed auto-upsell
  // won't be re-added by the SAME source (opt-out sticks), but a different source
  // added later can still auto-trigger the same item (NOT-306).
  const firedAutoUpsellKeys = useRef<Set<string>>(new Set());
  const handleAddPickedLineItem = useCallback(
    (picked: Parameters<typeof addPickedLineItem>[0]) => {
      userAddedSourceIds.current.add(picked.lineItemId);
      addPickedLineItem(picked);
    },
    [addPickedLineItem],
  );

  /**
   * What the item searches must not offer again — NOT-638.
   *
   * A MATERIAL or EQUIPMENT already on the estimate is deliberately absent from
   * this list. Since NOT-590 a picked service materializes a real line per
   * included part, so excluding what the document carries hid a service's own
   * fittings from the Materials and Equipment searches, and a tech who used one
   * more than the service assumes was left with a custom line — which drops
   * `lineItemId` and the cost tracking with it. Adding one appends a line here:
   * `addPickedLineItem` never merges, each row gets its own `_rowId` and
   * `lineKey`, and the stored array has no uniqueness rule.
   *
   * Everything else stays excluded, MEMBERSHIP above all: the server rejects an
   * estimate carrying two selected lines for one plan, and a membership line
   * cannot be deselected in the editor, so a duplicate would simply stop the
   * estimate saving. `canRepeatOnDocument` holds the per-type reasoning.
   */
  const pickerExcludeIds = useMemo(() => nonRepeatableLineItemIds(lineItems), [lineItems]);

  const addUpsellLine = useCallback(
    (upsell: EstimateUpsell) => {
      setLineItems((prev) => {
        // Dedup: never add an item already on the estimate.
        if (prev.some((li) => li.lineItemId === upsell.suggestedItem.id)) return prev;
        return [...prev, upsellToEditable(upsell)];
      });
    },
    [setLineItems],
  );

  // Source items currently ON the estimate. A debounced upsell result is keyed
  // on an older source set, so gating on "still present" (not just "ever added")
  // stops a stale result from auto-inserting for a source the user already
  // removed (NOT-306).
  const currentSourceIds = useMemo(
    () => new Set(lineItems.filter((li) => !li.isUpsell).map((li) => li.lineItemId)),
    [lineItems],
  );

  // When a source leaves the estimate, drop its consumed (source::item) pairs so
  // re-adding that source later runs its auto-upsell again — while a source that
  // STAYS keeps its pairs consumed (removing the upsell alone still sticks).
  useEffect(() => {
    for (const key of firedAutoUpsellKeys.current) {
      const sourceId = key.slice(0, key.indexOf('::'));
      if (!currentSourceIds.has(sourceId)) firedAutoUpsellKeys.current.delete(key);
    }
  }, [currentSourceIds]);

  // An upsell auto-fires when a source the user added THIS session (and that is
  // still on the estimate) marked it `auto` — per-source, never inheriting
  // another source's auto decision.
  const isAutoForUser = useCallback(
    (u: EstimateUpsell) =>
      u.sources.some(
        (s) =>
          s.auto &&
          userAddedSourceIds.current.has(s.lineItemId) &&
          currentSourceIds.has(s.lineItemId),
      ),
    [currentSourceIds],
  );

  useEffect(() => {
    if (upsells.length === 0) return;
    // Carry the FIRING source's copy so the persisted upsellContext reflects the
    // source that actually made it auto (not the aiWeight winner's).
    const additions: { upsell: EstimateUpsell; reason: string | null }[] = [];
    for (const u of upsells) {
      // Sources the user added that are still present and marked this auto.
      const firing = u.sources.filter(
        (s) =>
          s.auto &&
          userAddedSourceIds.current.has(s.lineItemId) &&
          currentSourceIds.has(s.lineItemId),
      );
      const freshKeys = firing
        .map((s) => `${s.lineItemId}::${u.suggestedItem.id}`)
        .filter((k) => !firedAutoUpsellKeys.current.has(k));
      if (freshKeys.length === 0) continue;
      // Mark fired up front so removing the line never re-adds it for these
      // sources; a future new source yields a new (unfired) pair.
      freshKeys.forEach((k) => firedAutoUpsellKeys.current.add(k));
      additions.push({ upsell: u, reason: firing[0]?.reason ?? u.reason });
    }
    if (additions.length === 0) return;
    setLineItems((prev) => {
      const present = new Set(prev.map((li) => li.lineItemId));
      const toAdd = additions
        .filter((a) => !present.has(a.upsell.suggestedItem.id))
        .map((a) => upsellToEditable(a.upsell, a.reason));
      return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
    });
  }, [upsells, currentSourceIds, setLineItems]);

  // The recommendation card lists candidates that won't auto-insert. Show the
  // copy/type of the source that's actually on the estimate (a user-added,
  // still-present source) — not the aiWeight winner's, which may differ.
  const recommendations = useMemo(
    () =>
      upsells
        .filter((u) => !isAutoForUser(u))
        .map((u) => {
          const eff =
            u.sources.find(
              (s) =>
                userAddedSourceIds.current.has(s.lineItemId) && currentSourceIds.has(s.lineItemId),
            ) ?? u.sources[0];
          return eff ? { ...u, reason: eff.reason, relationshipType: eff.relationshipType } : u;
        }),
    [upsells, isAutoForUser, currentSourceIds],
  );

  const {
    payload,
    customFieldValues,
    handleToggleAddressMode,
    handleSelectServiceAddress,
    handleSaStateChange,
    handleSaZipChange,
    handleToggleBillingMode,
    handleSelectBillingAddress,
    handleBaStateChange,
    handleBaZipChange,
  } = useEstimateFormViewModel({
    contactId,
    billToId,
    // NOT-218: only send a client override when the user actually edited the
    // number. An untouched preview is omitted so the server's collision-safe
    // generator runs and advances the counter (no duplicates).
    estimateNumber: isEditMode || estimateNumberDirty ? estimateNumber : '',
    title,
    jobTypeId,
    jobTypeName: selectedJobTypeName,
    poNumber,
    // Creation always states the unit — the picker locks once lines are added,
    // but the choice taken while the form was empty is exactly what must be
    // saved. An edit states it only while the STORED estimate would still
    // accept one; see `businessUnitSavable`.
    includeBusinessUnit: !hasNoBusinessUnits && businessUnitSavable,
    businessUnitId: effectiveBusinessUnitId,
    derivedBusinessUnitId,
    // The create-flow pick, kept apart from the override above: it needs no
    // dispatcher permission and stamps its own source.
    selectedBusinessUnitId: hasNoBusinessUnits ? undefined : selectedBusinessUnitId,
    includeTitle: isEditMode || Boolean(duplicateId) || titleDirty || titleNamesTheWork,
    baseMetadata: isEditMode ? (existingEstimate as Estimate | undefined)?.metadata : undefined,
    linkedLeadId,
    source,
    leadSource,
    leadSourceId,
    salespersonId,
    customerName,
    customerEmail,
    customerPhone,
    primaryContactName,
    primaryContactTitle,
    customerAddress,
    saStreet,
    saStreet2,
    saCity,
    saState,
    saZip,
    billingMode,
    selectedBillingIndex,
    baStreet,
    baStreet2,
    baCity,
    baState,
    baZip,
    storedBillingAddress: isEditMode
      ? ((existingEstimate as Estimate | undefined)?.billingAddress ?? null)
      : null,
    contactAddresses,
    lineItems,
    lineItemPreviews,
    subtotal,
    taxTotal,
    discountAmount,
    discountValue,
    discountType,
    discountReason,
    promotionId,
    pricingMode,
    customerApprovalMethod,
    requireSignature,
    grandTotal,
    depositAmount,
    depositRequired,
    depositType,
    depositValue,
    bookingType,
    recurringConfigPayload,
    validUntil,
    scopeOfWork,
    notes,
    terms,
    docTemplateId,
    internalNotes,
    tagIds,
    pendingAttachmentId,
    isEditMode,
    customFields,
    activeFieldKeys,
    estimateCustomFields,
    addressMode,
    selectedAddressIndex,
    applyAddress: applyAddressWithTax,
    handleZipChange,
    setAddressMode,
    setSelectedAddressIndex,
    setSaStreet,
    setSaStreet2,
    setSaCity,
    setSaState,
    setSaZip,
    setBillingMode,
    setSelectedBillingIndex,
    setBaStreet,
    setBaStreet2,
    setBaCity,
    setBaState,
    setBaZip,
    setCustomerAddress,
  });
  const { serviceAddressErrors, billingAddressErrors, addressValidationError } =
    resolveEstimateAddressValidation({
      addressMode,
      selectedAddressIndex,
      manualServiceAddress: {
        street: saStreet,
        street2: saStreet2,
        city: saCity,
        state: saState,
        zip: saZip,
      },
      billingMode,
      selectedBillingIndex,
      manualBillingAddress: {
        street: baStreet,
        street2: baStreet2,
        city: baCity,
        state: baState,
        zip: baZip,
      },
      billToId,
      contactAddresses,
    });
  // The one submit decision for this form. Declared here (above the edit-mode
  // loading early return) so the hook order stays stable, and shared by the
  // action buttons and the submit flow itself. The create-mode payment schedule
  // editor is local state and does not write an estimate.
  // Is the deposit being SET by this save? The saved estimate's deposit (or
  // none, for a new one) is the baseline; the bound is judged only against a
  // change — see `depositChanged` on the gate input.
  // The baseline is what the form HYDRATED from, not the raw columns: a legacy
  // row with no deposit type is opened as '$' + depositAmount, and comparing
  // against the columns called that "changed" on open.
  const savedDepositForm = existingEstimate
    ? deriveEstimateFormValuesFromEstimate(existingEstimate as Estimate)
    : null;
  // Switching the deposit ON counts (the server's `setsDeposit` does too);
  // switching it OFF does not — the figure is the one already stored, and the
  // bound no longer depends on the switch, so that would refuse a re-send.
  const depositChanged =
    !savedDepositForm ||
    (depositRequired && !savedDepositForm.depositRequired) ||
    savedDepositForm.depositType !== depositType ||
    savedDepositForm.depositValue !== depositValue;
  // Always price the plan against the total on screen. The server preview
  // resolves percentages against the LAST-SAVED grandTotal, so editing line
  // items left the stages quoting an old figure while the totals block above
  // them showed the new one. Falling back to the saved template here keeps one
  // number on the page.
  // §8 — the tenant can hide the plan from the customer. The server honours that
  // for its own preview, so building one client-side has to honour it too or the
  // PDF prints a plan the tenant chose to withhold.
  const showPaymentPlanPreview =
    estimateSettings?.paymentSchedule?.showPaymentPlanPreview !== false;
  // The office's own tile. `showPaymentPlanPreview` hides the plan from the
  // CUSTOMER, so gating this too would blank the builder for staff — the same
  // defect the audience split fixes server-side.
  const draftStagesForTile = useMemo(() => {
    const source =
      effectiveDraftPaymentSchedule ??
      (existingScheduleTemplate ? paymentScheduleTemplateToDraft(existingScheduleTemplate) : null);
    return (
      draftPaymentPlanPreview(source, grandTotal, depositRequired ? depositAmount : 0)?.stages ?? []
    );
  }, [
    effectiveDraftPaymentSchedule,
    existingScheduleTemplate,
    grandTotal,
    depositRequired,
    depositAmount,
  ]);
  const draftPreviewPaymentPlan = useMemo(() => {
    if (!showPaymentPlanPreview) return null;
    const source =
      effectiveDraftPaymentSchedule ??
      (existingScheduleTemplate ? paymentScheduleTemplateToDraft(existingScheduleTemplate) : null);
    return draftPaymentPlanPreview(source, grandTotal, depositRequired ? depositAmount : 0);
  }, [
    showPaymentPlanPreview,
    effectiveDraftPaymentSchedule,
    existingScheduleTemplate,
    grandTotal,
    depositRequired,
    depositAmount,
  ]);
  const paymentScheduleValidationError = useMemo(() => {
    // Also validate a SAVED template. Bailing on a null draft meant editing a
    // saved estimate's deposit was never checked here, so the first sign of a
    // conflict was the server's rejection on save.
    const source =
      effectiveDraftPaymentSchedule ??
      (existingScheduleTemplate ? paymentScheduleTemplateToDraft(existingScheduleTemplate) : null);
    if (!source) return null;
    return (
      validateDraftEstimatePaymentSchedule(source, grandTotal, {
        required: depositRequired,
        amount: depositAmount,
      })[0] ?? null
    );
  }, [
    effectiveDraftPaymentSchedule,
    existingScheduleTemplate,
    grandTotal,
    depositRequired,
    depositAmount,
  ]);
  const submitGate = useMemo(
    () =>
      evaluateEstimateSubmitGate({
        businessUnitBlockedReason,
        contactId,
        customerName,
        lineItems,
        pricingMode,
        membershipPricingState: membershipPricingError
          ? 'error'
          : membershipSettled
            ? 'ready'
            : 'loading',
        addressValidationError,
        discountType,
        discountValue,
        subtotal,
        depositRequired,
        depositType,
        depositValue,
        depositEligibleTotal,
        depositChanged,
        paymentScheduleValidationError,
      }),
    [
      businessUnitBlockedReason,
      contactId,
      customerName,
      lineItems,
      pricingMode,
      membershipPricingError,
      membershipSettled,
      addressValidationError,
      discountType,
      discountValue,
      subtotal,
      depositRequired,
      depositType,
      depositValue,
      depositEligibleTotal,
      depositChanged,
      paymentScheduleValidationError,
    ],
  );
  const canSaveEstimate = canSubmitEstimate(submitGate);
  const saveBlockedReason = estimateSubmitBlockedReason(submitGate);
  const invalidateEstimateCustomFieldValues = useCallback(
    (estimateId: string) => {
      void queryClient.invalidateQueries({
        queryKey: customFieldKeys.values('estimate', estimateId),
      });
    },
    [queryClient],
  );
  const pendingLineItemFilesRef = useRef<Map<string, File[]>>(new Map());

  const onAfterCreate = useCallback(async (estimateId: string) => {
    for (const [rowId, files] of pendingLineItemFilesRef.current) {
      for (const file of files) {
        try {
          const { uploadUrl } = await apiPost<{ uploadUrl: string }>(
            operationsClient,
            opsEstimateEndpoints.attachmentUploadUrl(estimateId),
            { filename: file.name, contentType: file.type, fileSize: file.size, lineItemId: rowId },
          );
          await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          });
        } catch {
          // silent — attachment is optional
        }
      }
    }
  }, []);

  // Editing an APPROVED estimate is allowed (the guard above admits it while
  // the approval has not become a job), and SAVING dismisses the approval.
  // The form says so up front and the save stops for a confirmation.
  const editingApprovedEstimate =
    isEditMode && (existingEstimate as Estimate | undefined)?.status === 'APPROVED';
  const {
    submitting,
    savePending,
    sendPending,
    showSendConfirm,
    showDismissApprovalConfirm,
    handleSubmit,
    handleSubmitAndSend,
    handleSendConfirm,
    handleSendCancel,
    handleDismissApprovalConfirm,
    handleDismissApprovalCancel,
  } = useEstimateSubmitFlow({
    submitGate,
    isEditMode,
    editId,
    requiresApprovalDismissal: editingApprovedEstimate,
    duplicateId,
    templateId,
    payload: {
      ...payload,
      ...(!isEditMode && duplicateId ? { duplicateFromEstimateId: duplicateId } : {}),
      ...(!isEditMode &&
      duplicateId &&
      (!paymentSchedulesAllowed || removeDuplicatedPaymentSchedule)
        ? { copyDuplicatePaymentSchedule: false }
        : {}),
      ...(!isEditMode && sourceJobId
        ? {
            sourceJobId,
            fulfillmentMode: fulfillmentMode ?? 'UNDECIDED',
            ...(originAppointmentId ? { originAppointmentId } : {}),
          }
        : {}),
      ...(!isEditMode && paymentSchedulesAllowed && draftPaymentSchedule
        ? { paymentScheduleTemplate: draftPaymentSchedule }
        : {}),
    } as CreateEstimateDto,
    customerApprovalMethod,
    requireSignature,
    setRequireSignature,
    onAddressValidationFailed: () => setShowAddressValidationErrors(true),
    customFieldValues,
    excludedTemplateAttachmentIds,
    excludedDuplicateAttachmentIds,
    createEstimate,
    updateEstimate: submitUpdateEstimate,
    sendEstimate,
    upsertValues,
    invalidateEstimateCustomFieldValues,
    navigateToEstimateDetail: (id) =>
      void router.navigate({
        to: '/dashboard/estimates/$id',
        params: { id },
        search: { tab: 'overview' } as never,
      }),
    navigateToEstimateEdit: (id) =>
      void router.navigate({
        to: '/dashboard/estimates/$id/edit',
        params: { id },
        search: {} as never,
      }),
    onAfterCreate,
  });
  const [showPreview, setShowPreview] = useState(false);
  const handlePreview = useCallback(() => {
    setShowPreview(true);
  }, []);

  // Click adapters for the two write entry points. Each flow rethrows on a
  // failed create so `runCreateFlow` can release its lock; the mutation layer
  // (useApiMutation, `showErrorToast` defaults to true) has already toasted by
  // then, so swallowing here costs no feedback and avoids an unhandled rejection.
  const handleSaveClick = useCallback(() => {
    void handleSubmit().catch(() => {});
  }, [handleSubmit]);
  const handleSaveAndSendClick = useCallback(() => {
    void handleSubmitAndSend().catch(() => {});
  }, [handleSubmitAndSend]);
  const handleConfigureScheduleClick = useCallback(() => {
    if (isEditMode && editId) {
      void router.navigate({
        to: '/dashboard/estimates/$id/payment-schedule',
        params: { id: editId },
        search: {} as never,
      });
      return;
    }
    setPaymentScheduleDialogOpen(true);
  }, [isEditMode, editId, router]);

  const draftPdfCustomFields = useMemo<PdfCustomFieldValue[]>(() => {
    const valueByDefinitionId = new Map(
      customFieldValues.map((value) => [value.fieldDefinitionId, value.value]),
    );

    return estimateCustomFields
      .filter((schema) => schema.association.isCustomerFacing)
      .flatMap((schema) => {
        const value = valueByDefinitionId.get(schema.definition.id);
        if (value == null || value === '') return [];
        const formatted = formatCustomFieldValueForPdf(
          schema.definition.fieldType,
          value,
          (schema.definition.config ?? {}) as Record<string, unknown>,
        );
        if (!formatted) return [];
        return [
          {
            fieldDefinitionId: schema.definition.id,
            label: schema.definition.label,
            value: formatted,
          },
        ];
      });
  }, [estimateCustomFields, customFieldValues]);

  const previewEstimate = useMemo<Estimate>(() => {
    const existing = existingEstimate as Estimate | undefined;
    const now = new Date().toISOString();
    return {
      ...(existing ?? {}),
      id: existing?.id ?? 'draft-estimate-preview',
      tenantId: existing?.tenantId ?? 'draft',
      estimateNumber:
        estimateNumber.trim() ||
        payload.estimateNumber?.trim() ||
        existing?.estimateNumber ||
        'Auto-generated on save',
      title: payload.title ?? existing?.title ?? null,
      status: existing?.status ?? 'DRAFT',
      customer: payload.customer,
      contactId: payload.contactId ?? contactId,
      serviceAddress: payload.serviceAddress ?? null,
      lineItems: payload.lineItems ?? [],
      subtotal: payload.subtotal ?? 0,
      taxTotal: payload.taxTotal ?? 0,
      discountTotal: payload.discountTotal ?? 0,
      discountValue: payload.discountValue ?? 0,
      promotionId: payload.promotionId ?? null,
      membershipDiscountTotal: existing?.membershipDiscountTotal ?? 0,
      depositAmount: payload.depositAmount ?? 0,
      grandTotal: payload.grandTotal ?? 0,
      validUntil: payload.validUntil ?? null,
      scopeOfWork: payload.scopeOfWork ?? existing?.scopeOfWork ?? null,
      notes: payload.notes ?? null,
      internalNotes: payload.internalNotes ?? null,
      // From the selection, NOT the save payload — the payload carries no text
      // for DEFAULT/TEMPLATE (the server resolves it), which left the preview's
      // Terms & Conditions section blank for everyone who did not type custom
      // terms, even though the form and the saved estimate both showed them.
      ...toTermsPreviewFields(terms),
      signatureRequired: payload.signatureRequired ?? true,
      depositRequired: payload.depositRequired ?? false,
      depositType: payload.depositType ?? null,
      depositValue: payload.depositValue ?? 0,
      depositCollected: existing?.depositCollected ?? false,
      bookingType: payload.bookingType ?? 'ONE_OFF',
      recurringConfig: payload.recurringConfig ?? null,
      customerApprovalMethod: payload.customerApprovalMethod ?? null,
      pricingMode: payload.pricingMode ?? 'STANDARD',
      billingAddress: payload.billingAddress ?? null,
      tags: payload.tagIds?.map((tagId) => ({ tagId })) ?? existing?.tags ?? [],
      metadata: payload.metadata ?? existing?.metadata,
      draftVersion: existing?.draftVersion ?? 1,
      revisionNumber: existing?.revisionNumber ?? 0,
      previousVersions: existing?.previousVersions ?? [],
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      activePdf: undefined,
    } as Estimate;
  }, [contactId, existingEstimate, payload, estimateNumber, terms]);
  const previewDownloadFileName = useMemo(() => {
    const number =
      previewEstimate.estimateNumber === 'Auto-generated on save'
        ? 'preview'
        : previewEstimate.estimateNumber;
    const safeNumber = number.trim().replace(/[^a-z0-9_-]+/gi, '-') || 'preview';
    return `estimate-${safeNumber}.pdf`;
  }, [previewEstimate.estimateNumber]);

  // ── Loading state for edit mode ──────────────────────────────────────────
  if (
    (isEditMode && loadingEstimate) ||
    (duplicateId && (loadingDuplicate || loadingDuplicateSchedule))
  ) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading estimate...</span>
      </div>
    );
  }

  return (
    <>
      <FormPageLayout
        title={isEditMode ? 'Edit Estimate' : 'New Estimate'}
        onBack={() =>
          editId
            ? void router.navigate({
                to: '/dashboard/estimates/$id',
                params: { id: editId },
                search: { tab: 'overview' } as never,
              })
            : // @ts-expect-error — search required by TanStack Router v1.168
              void router.navigate({ to: '/dashboard/estimates' })
        }
        actions={
          <EstimateFormActions
            isEditMode={isEditMode}
            submitting={submitting}
            savePending={savePending}
            sendPending={sendPending}
            canSave={canSaveEstimate}
            blockedReason={saveBlockedReason}
            onRetryBlocked={membershipPricingError ? retryMembershipPricing : undefined}
            onCancel={() =>
              editId
                ? void router.navigate({
                    to: '/dashboard/estimates/$id',
                    params: { id: editId },
                    search: { tab: 'overview' } as never,
                  })
                : // @ts-expect-error — search required by TanStack Router v1.168
                  void router.navigate({ to: '/dashboard/estimates' })
            }
            onSave={handleSaveClick}
            onSaveAndSend={handleSaveAndSendClick}
            onPreview={handlePreview}
          />
        }
      >
        <div className="min-h-full bg-muted/40">
          <div className="mx-auto max-w-6xl space-y-3 px-4 py-5">
            {editingApprovedEstimate ? (
              <Alert variant="warning">
                <AlertTriangle className="size-4" />
                <AlertTitle>This estimate is approved</AlertTitle>
                <AlertDescription>
                  Saving changes dismisses the approval. The approved version is kept as a previous
                  revision, the customer&apos;s signature is cleared, and the estimate returns to
                  Revised so it can be sent and approved again. Nothing changes until you save.
                </AlertDescription>
              </Alert>
            ) : null}
            <EstimateFormSections
              businessUnitBlockedReason={businessUnitBlockedReason}
              sections={sections}
              onToggleSection={toggleSection}
              estimateNumber={estimateNumber}
              onEstimateNumberChange={handleEstimateNumberChange}
              title={title}
              onTitleChange={handleTitleChange}
              jobTypeId={jobTypeId}
              jobTypeName={selectedJobTypeName}
              jobTypes={selectableJobTypes}
              jobTypesLoading={jobTypesLoading}
              jobTypesFailed={jobTypesFailed}
              onJobTypeChange={setJobTypeId}
              docTemplates={docTemplates}
              docTemplateId={docTemplateId}
              onDocTemplateChange={setDocTemplateId}
              poNumber={poNumber}
              onPoNumberChange={setPoNumber}
              commercialIdentityLocked={Boolean(
                (existingEstimate as Estimate | undefined)?.originJobId,
              )}
              isEditMode={isEditMode}
              businessUnitProps={{
                mode: 'assign',
                locked: businessUnitLocked,
                derivedBusinessUnitId: effectiveBusinessUnitId ? derivedBusinessUnitId : null,
                candidateCount: businessUnitCandidateIds.length,
                value: effectiveBusinessUnitId,
                onChange: setBusinessUnitId,
              }}
              detailsProps={{
                validUntil,
                onValidUntilChange: setValidUntil,
                tagIds,
                onTagIdsChange: setTagIds,
              }}
              customerProps={{
                contactId,
                selectedCustomer,
                billToId,
                onBillToIdChange: setBillToId,
                onResetBillingToService: handleResetBillingToService,
                customerEmail,
                customerPhone,
                isEditMode,
                onSelectContact: handleSelectContact,
                onClearContact: handleClearContact,
                membershipInfo,
                inCartOverriddenByActive,
                onEmailChange: setCustomerEmail,
                onPhoneChange: setCustomerPhone,
                contactAddresses,
                addressMode,
                selectedAddressIndex,
                showAllAddresses,
                onToggleAddressMode: handleToggleAddressMode,
                onSelectServiceAddress: handleSelectServiceAddress,
                onToggleShowAllAddresses: () => setShowAllAddresses((prev) => !prev),
                saStreet,
                saStreet2,
                saCity,
                saState,
                saZip,
                onSaStreetChange: setSaStreet,
                onSaStreet2Change: setSaStreet2,
                onSaCityChange: setSaCity,
                onSaStateChange: handleSaStateChange,
                onSaZipChange: handleSaZipChange,
                serviceAddressErrors,
                billingMode,
                selectedBillingIndex,
                showAllBillingAddresses,
                onToggleBillingMode: handleToggleBillingMode,
                onSelectBillingAddress: handleSelectBillingAddress,
                onToggleShowAllBillingAddresses: () => setShowAllBillingAddresses((prev) => !prev),
                baStreet,
                baStreet2,
                baCity,
                baState,
                baZip,
                onBaStreetChange: setBaStreet,
                onBaStreet2Change: setBaStreet2,
                onBaCityChange: setBaCity,
                onBaStateChange: handleBaStateChange,
                onBaZipChange: handleBaZipChange,
                billingAddressErrors,
                showAddressErrors: showAddressValidationErrors,
              }}
              membershipRecommendation={
                // Once this plan is already a line, it is no longer a
                // recommendation. Keeping this card but removing its CTA left
                // a dead-looking offer; restoring Add would create a duplicate
                // membership the server correctly rejects. The optional line
                // still carries the customer-facing offer and its own edit
                // control below.
                membershipOffer && !recommendedMembershipAlreadyAdded ? (
                  <MembershipOfferBlock
                    offer={membershipOffer}
                    onAddMembership={openRecommendedMembership}
                  />
                ) : null
              }
              lineItemsProps={{
                picker: (
                  <LineItemPicker
                    businessUnitId={pricebookBusinessUnitId}
                    onAdd={handleAddPickedLineItem}
                    excludeIds={pickerExcludeIds}
                    membershipCustomerId={contactId}
                    membershipServiceAddressId={inheritanceServiceAddressId}
                    // The estimate is where the sold membership is provisioned
                    // from, so it is the one document that can answer for a
                    // visit the tech already performed.
                    allowDeliveredVisits
                    // NOT-590 — picking a service that includes parts adds the
                    // service line plus a real priced line per part.
                    expandComponents
                    // Standalone materials/equipment are costs until the
                    // operator explicitly marks their line Chargeable.
                    partsStartUnbilled
                  />
                ),
                // A part the SERVICE did not come with. It joins what the service
                // contains — so the service reprices from it — and lands as an
                // ordinary line whose money is inside the service, exactly like
                // a part the catalog put there.
                componentPicker: (_parent, parentIndex) => (
                  <LineItemPicker
                    businessUnitId={pricebookBusinessUnitId}
                    onAdd={(picked) => addConfiguredServicePart(parentIndex, picked)}
                    allowedTypes={['MATERIAL', 'EQUIPMENT']}
                    placeholder="Add a material or equipment…"
                  />
                ),
                removeConfiguredPart: removeConfiguredServicePart,
                // Each section searches only its own kind, so you never have to
                // tell a "Drain line" service from a "Drain line" material in a
                // single mixed result list. The Materials and Equipment sections
                // are where NOT-638 bit, and where `pickerExcludeIds` now leaves
                // the catalog open.
                sectionedPicker: (section) => (
                  <LineItemPicker
                    businessUnitId={pricebookBusinessUnitId}
                    onAdd={handleAddPickedLineItem}
                    membershipEditRequest={
                      section.id === 'memberships' ? membershipEditRequest : null
                    }
                    membershipAddRequest={
                      section.id === 'memberships' ? membershipAddRequest : null
                    }
                    onEditMembership={
                      section.id === 'memberships' ? applyMembershipConfiguration : undefined
                    }
                    excludeIds={pickerExcludeIds}
                    expandComponents
                    allowedTypes={section.allowedTypes}
                    placeholder={section.placeholder}
                    membershipCustomerId={contactId}
                    membershipServiceAddressId={inheritanceServiceAddressId}
                    allowDeliveredVisits
                    partsStartUnbilled
                  />
                ),
                lineItems,
                lineItemPreviews,
                pricingMode,
                estimateId: editId,
                updateQuantity,
                updateName,
                updateType,
                updateUnit,
                updateBasePrice,
                updateManualPriceOverride,
                updatePriceStrategy,
                updateLaborRate,
                updateEstimatedHours,
                updateLaborCostLines,
                laborRateProfiles,
                updateDynamicPricingConfig,
                updateDynamicMarkupProfile,
                updateDescription,
                updateEquipmentAssociation,
                updateMembershipEquipmentAssociation,
                onConfigureMembership: openMembershipConfigurator,
                membershipEquipmentPricingStatusByRowId,
                updateComponentDisplayMode,
                customerId: contactId,
                serviceAddressId: inheritanceServiceAddressId,
                updateMarkupPercent,
                updateCostBasis,
                updateTaxable,
                updateTaxAccounting,
                updateMemberPricing,
                onSaveLineToPricebook: linkLineToCatalogItem,
                updateChargeable,
                updateHiddenFromCustomer,
                toggleOptional,
                toggleSelected,
                removeLineItem,
                onReorder: reorderLineItems,
                membershipEligibleRowIds,
                membershipInfo: membershipInfo
                  ? {
                      planName: membershipInfo.planName,
                      discountType: membershipInfo.discountType,
                      discountValue: membershipInfo.discountValue,
                    }
                  : null,
                onPendingFilesChange: (rowId, files) => {
                  pendingLineItemFilesRef.current.set(rowId, files);
                },
              }}
              upsellRecommendations={
                <EstimateUpsellRecommendations
                  recommendations={recommendations}
                  onAdd={addUpsellLine}
                />
              }
              financialsProps={{
                discountValue,
                discountType,
                discountReason,
                promotionId,
                showPromotionSelector: true,
                onPromotionChange: (nextPromotionId, patch) => {
                  setPromotionId(nextPromotionId);
                  if (!patch) return;
                  setDiscountType(patch.discountType);
                  setDiscountValue(patch.discountValue);
                  setDiscountReason(patch.discountReason);
                },
                onDiscountValueChange: setDiscountValue,
                onDiscountTypeChange: (next) => setDiscountType(next ?? '$'),
                onDiscountReasonChange: setDiscountReason,
                depositValue,
                depositType,
                depositRequired,
                onDepositValueChange: setDepositValue,
                onDepositTypeChange: setDepositType,
                onDepositRequiredChange: setDepositRequired,
                subtotal,
                discountAmount,
                depositAmount,
                grandTotal,
                depositEligibleTotal,
                enforceDepositBound: true,
                depositChanged,
                showTM: true,
                pricingMode,
                onPricingModeChange: changePricingMode,
                tmUnderquotedServices: underquotedTMServices,
                onTMRepriceFromParts: repriceTMFromParts,
                taxTotal,
                taxLoading,
                taxResolution: liveTaxResolution,
                taxAddressProvided: Boolean(saZip.trim() || saCity.trim()),
                taxBreakdown,
                // Tax Policy layer: once a LIVE resolution exists its match
                // is authoritative — including "no rule matches", or the
                // badge could disagree with the live totals. The frozen
                // taxSummary.policy only fills in before an address resolves.
                taxPolicy: liveTaxResolution
                  ? (liveTaxResolution.policy ?? null)
                  : ((
                      (existingEstimate as Estimate | undefined)?.taxSummary as
                        | {
                            policy?: {
                              id: string;
                              name: string;
                              taxableBases: string[];
                            } | null;
                          }
                        | null
                        | undefined
                    )?.policy ?? null),
                onChangeTaxGroup:
                  isEditMode && editId ? () => setTaxGroupDialogOpen(true) : undefined,
                membershipDiscountPreview,
                membershipInfo: membershipInfo ?? undefined,
              }}
              costPanelProps={{
                lineItems,
                pricingMode,
                subtotal,
                taxTotal,
                grandTotal,
              }}
              salesAttributionProps={{
                linkedLeadId,
                // The field is read-only, so this prop is what the user SEES,
                // not what gets saved — the payload keeps using `source`
                // state. Legacy rows carry no attribution but do carry a
                // platform origin, and that is the only thing standing
                // between them and a permanent "-".
                source: resolveEstimateSourceForDisplay(
                  source,
                  (existingEstimate as Estimate | undefined)?.source,
                ),
                leadSource,
                salespersonId,
                selectedLead,
                users,
                onLinkedLeadSelect: handleLinkedLeadSelect,
                onClearLinkedLead: handleClearLinkedLead,
                onLeadSourceChange: setLeadSource,
                onSalespersonChange: handleSalespersonChange,
              }}
              scheduleProps={{
                readOnly: Boolean(
                  isEditMode && (existingEstimate as Estimate | undefined)?.originJobId,
                ),
                bookingType,
                onBookingTypeChange: setBookingType,
                recurringStartDate,
                onRecurringStartDateChange: setRecurringStartDate,
                recurringDaysOfWeek,
                onRecurringDaysOfWeekChange: setRecurringDaysOfWeek,
                recurringFrequency,
                onRecurringFrequencyChange: setRecurringFrequency,
                recurringEndType,
                onRecurringEndTypeChange: setRecurringEndType,
                recurringInterval,
                onRecurringIntervalChange: setRecurringInterval,
                recurringIntervalUnit,
                onRecurringIntervalUnitChange: setRecurringIntervalUnit,
                recurringEndAfterCount,
                onRecurringEndAfterCountChange: setRecurringEndAfterCount,
                recurringEndOnDate,
                onRecurringEndOnDateChange: setRecurringEndOnDate,
              }}
              customerContentProps={{
                scopeOfWork,
                onScopeOfWorkChange: setScopeOfWork,
                notes,
                onNotesChange: setNotes,
                isEditMode,
                editId,
                pendingAttachmentId,
                attachments,
                onAttachmentsChange: handleCustomerAttachmentsChange,
              }}
              termsProps={{
                value: terms,
                onChange: setTerms,
              }}
              internalContentProps={{
                internalNotes,
                onInternalNotesChange: setInternalNotes,
                isEditMode,
                editId,
                pendingAttachmentId,
                internalAttachments,
                onInternalAttachmentsChange: setInternalAttachments,
              }}
              customFieldsProps={
                estimateCustomFields.length > 0
                  ? {
                      schemas: estimateCustomFields,
                      activeFieldKeys,
                      values: customFields,
                      onFieldChange: setCustomField,
                      onActiveFieldKeysChange: setActiveFieldKeys,
                      onRemoveField: removeField,
                    }
                  : undefined
              }
              paymentScheduleProps={
                isEditMode || paymentSchedulesAllowed
                  ? {
                      isEditMode,
                      estimateId: editId,
                      grandTotal,
                      estimateStatus: (existingEstimate as Estimate | undefined)?.status,
                      depositAmount,
                      depositRequired,
                      hasTemplate: Boolean(existingScheduleTemplate),
                      hasDraftTemplate: Boolean(effectiveDraftPaymentSchedule),
                      draftStages: draftStagesForTile,
                      onSetUpSchedule: handleConfigureScheduleClick,
                    }
                  : undefined
              }
            />
          </div>
        </div>
      </FormPageLayout>

      {!isEditMode && paymentSchedulesAllowed ? (
        <NewEstimatePaymentScheduleDialog
          open={paymentScheduleDialogOpen}
          onOpenChange={setPaymentScheduleDialogOpen}
          value={effectiveDraftPaymentSchedule}
          contactId={contactId || null}
          grandTotal={grandTotal}
          depositRequired={depositRequired}
          depositAmount={depositAmount}
          onSave={(schedule) => {
            setRemoveDuplicatedPaymentSchedule(false);
            setDraftPaymentSchedule(schedule);
          }}
          onRemove={() => {
            setDraftPaymentSchedule(null);
            setRemoveDuplicatedPaymentSchedule(Boolean(duplicateId));
          }}
        />
      ) : null}

      {/* Tax Policy layer (phase 4) — pin/clear the document's tax group. */}
      {isEditMode && editId ? (
        <TaxGroupOverrideDialog
          open={taxGroupDialogOpen}
          onClose={() => setTaxGroupDialogOpen(false)}
          document="estimate"
          documentId={editId}
          currentTaxGroupId={(existingEstimate as Estimate | undefined)?.taxGroupId ?? null}
          currentlyManual={
            (
              existingEstimate as Estimate | undefined as
                | { taxResolvedFrom?: string | null }
                | undefined
            )?.taxResolvedFrom === 'MANUAL'
          }
          onSaved={() => {
            // The server repriced the document; refetch AND re-apply the
            // fresh row onto the (once-only hydrated) form state.
            void queryClient.invalidateQueries({ queryKey: estimateKeys.all() });
            rehydrateEstimateForm();
          }}
        />
      ) : null}

      {/* Saving an APPROVED estimate dismisses its approval — confirm first. */}
      <AlertDialog
        open={showDismissApprovalConfirm}
        onOpenChange={(open) => {
          if (!open) handleDismissApprovalCancel();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dismiss the approval and save?</AlertDialogTitle>
            <AlertDialogDescription>
              This estimate was approved by the customer. Saving your changes archives the approved
              version as a previous revision, clears the approval and signature, and moves the
              estimate to Revised. You will need to send it for approval again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep approval</AlertDialogCancel>
            <AlertDialogAction onClick={handleDismissApprovalConfirm} disabled={submitting}>
              <RotateCcw className="mr-1 size-3.5" />
              Dismiss approval & save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── PDF preview dialog ──────────────────────────────────────────── */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent
          className="w-full max-w-4xl p-0"
          showCloseButton={false}
          onInteractOutside={() => setShowPreview(false)}
          onPointerDownOutside={() => setShowPreview(false)}
        >
          <DialogTitle className="sr-only">Estimate Preview</DialogTitle>
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              </div>
            }
          >
            <EstimatePDFPreview
              estimate={previewEstimate}
              draftCustomFieldValues={draftPdfCustomFields}
              downloadFileName={previewDownloadFileName}
              timeZone={companyTimeZone}
              // Reflect the design picked in the form (even before saving) rather
              // than the estimate's last-saved design.
              previewDocTemplateId={docTemplateId ?? null}
              // The plan the form currently holds. The server preview endpoint
              // needs a saved estimate, so on a new one this is the only source
              // — without it a configured schedule is missing from its preview.
              draftPaymentPlan={draftPreviewPaymentPlan}
            />
          </Suspense>
        </DialogContent>
      </Dialog>

      {/* ── Send confirmation modal ──────────────────────────────────────── */}
      <SendConfirmModal
        open={showSendConfirm}
        title="Send Estimate"
        customerName={customerName}
        customerEmail={customerEmail || undefined}
        customerPhone={customerPhone || undefined}
        approvalMethod={customerApprovalMethod}
        onApprovalMethodChange={handleApprovalMethodChange}
        requireSignature={requireSignature}
        onRequireSignatureChange={setRequireSignature}
        confirmLabel="Send"
        cancelLabel="Save as Draft"
        isPending={sendEstimate.isPending}
        onConfirm={handleSendConfirm}
        onCancel={handleSendCancel}
      />
    </>
  );
}
